import React, { useState, useEffect } from 'react';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import LearnMathsSection from './components/LearnMathsSection';
import YouTubeSection from './components/YouTubeSection';
import ShopSection from './components/ShopSection';
import Playground from './components/Playground';
import Curriculum from './components/Curriculum';
import Printables from './components/Printables';
import Safety from './components/Safety';
import ContactSection from './components/ContactSection';
import PrivacyPolicySection from './components/PrivacyPolicySection';
import Footer from './components/Footer';
import ParentGateModal from './components/ParentGateModal';

function getRoute() {
  const pathname = (window.location.pathname || '').toLowerCase();
  const hash = (window.location.hash || '').toLowerCase();

  // Check if current route is under kikiandkids/maths/ or /maths/
  if (
    pathname.includes('maths/privacy') || 
    pathname.includes('kikiandkids/maths/privacy') ||
    hash.includes('maths-privacy') ||
    hash.includes('/maths/privacy') ||
    hash.includes('/kikiandkids/maths/privacy')
  ) {
    return 'maths-privacy';
  }

  if (
    pathname.includes('maths/contact') || 
    pathname.includes('kikiandkids/maths/contact') ||
    hash.includes('maths-contact') ||
    hash.includes('/maths/contact') ||
    hash.includes('/kikiandkids/maths/contact')
  ) {
    return 'maths-contact';
  }

  return 'home';
}

export default function App() {
  const [currentRoute, setCurrentRoute] = useState(getRoute);
  const [parentGateOpen, setParentGateOpen] = useState(false);

  useEffect(() => {
    const handleLocationChange = () => {
      setCurrentRoute(getRoute());
    };

    window.addEventListener('popstate', handleLocationChange);
    window.addEventListener('hashchange', handleLocationChange);

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      window.removeEventListener('hashchange', handleLocationChange);
    };
  }, []);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // STANDALONE ROUTE: /kikiandkids/maths/privacy or /maths/privacy
  if (currentRoute === 'maths-privacy') {
    return (
      <div className="min-h-screen flex flex-col bg-slate-50 text-slate-800 antialiased">
        <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b-2 border-slate-200 py-3.5 px-4 sm:px-8">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/assets/images/app-icon-math.png" alt="Learn Maths" className="w-10 h-10 rounded-xl shadow-xs" />
              <div>
                <span className="font-heading font-black text-lg text-slate-900 block leading-tight">
                  Kiki and Kids – Learn Maths
                </span>
                <span className="text-[11px] font-bold text-kiki-blue uppercase tracking-wider block">
                  iOS &amp; iPadOS Privacy Policy
                </span>
              </div>
            </div>
            <a
              href="/"
              className="text-xs font-heading font-bold text-slate-600 hover:text-kiki-blue px-3 py-1.5 rounded-full border border-slate-200 hover:bg-slate-50 transition-all"
            >
              ← Back to Portal
            </a>
          </div>
        </header>

        <main className="flex-1">
          <PrivacyPolicySection />
        </main>

        <footer className="py-8 bg-slate-900 text-slate-400 text-xs text-center border-t border-slate-800">
          <p>© {new Date().getFullYear()} Kiki and Kids – Learn Maths. Official iOS Application Privacy Policy.</p>
        </footer>
      </div>
    );
  }

  // STANDALONE ROUTE: /kikiandkids/maths/contact or /maths/contact
  if (currentRoute === 'maths-contact') {
    return (
      <div className="min-h-screen flex flex-col bg-white text-slate-800 antialiased">
        <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b-2 border-slate-200 py-3.5 px-4 sm:px-8">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/assets/images/app-icon-math.png" alt="Learn Maths" className="w-10 h-10 rounded-xl shadow-xs" />
              <div>
                <span className="font-heading font-black text-lg text-slate-900 block leading-tight">
                  Kiki and Kids – Learn Maths
                </span>
                <span className="text-[11px] font-bold text-kiki-blue uppercase tracking-wider block">
                  Support &amp; Family Inquiries
                </span>
              </div>
            </div>
            <a
              href="/"
              className="text-xs font-heading font-bold text-slate-600 hover:text-kiki-blue px-3 py-1.5 rounded-full border border-slate-200 hover:bg-slate-50 transition-all"
            >
              ← Back to Portal
            </a>
          </div>
        </header>

        <main className="flex-1">
          <ContactSection />
        </main>

        <footer className="py-8 bg-slate-900 text-slate-400 text-xs text-center border-t border-slate-800">
          <p>© {new Date().getFullYear()} Kiki and Kids – Learn Maths. Official iOS Application Support.</p>
        </footer>
      </div>
    );
  }

  // MAIN PAGE ROUTE (No contact us or privacy policy linked on main page)
  return (
    <div className="min-h-screen flex flex-col bg-[#FFFDF9] text-slate-800 antialiased selection:bg-kiki-yellow selection:text-slate-900">
      {/* Top Navbar: only Learn Maths, YouTube, Shop, Playground, Parents */}
      <Navbar
        onOpenParentGate={() => setParentGateOpen(true)}
        onNavigate={scrollToSection}
      />

      {/* Main Content Sections */}
      <main className="flex-1">
        {/* Hero / Portal Overview */}
        <Hero />

        {/* 1. Kiki and Kids – Learn Maths (Clean showcase without contact/privacy buttons) */}
        <LearnMathsSection />

        {/* 2. Kiki and Kids YouTube (Original Home Moved Here: Telugu/Tamil, Miss Kiki, Words Table) */}
        <YouTubeSection />

        {/* 3. Kiki and Kids Shop (New Section Selling Kids Toys, Blocks & Books) */}
        <ShopSection />

        {/* 4. Interactive Browser Playground: Bubble Pop, Island Mini-Math, Soundboard */}
        <Playground />

        {/* 5. Curriculum & No-Failure Pedagogy */}
        <Curriculum />

        {/* 6. Free Family Activity Printables */}
        <Printables />

        {/* 7. Child Safety & Offline Manifesto */}
        <Safety />
      </main>

      {/* Footer without contact or privacy links */}
      <Footer onOpenParentGate={() => setParentGateOpen(true)} />

      {/* Gated Parent Dashboard Modal */}
      <ParentGateModal
        isOpen={parentGateOpen}
        onClose={() => setParentGateOpen(false)}
      />
    </div>
  );
}
